import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchAdminPaymentRequests } from '../../redux/adminPaymentSlice';
import { simulationApi } from '../../services/api';
import './SimulationPage.css';

interface SimulationFormData {
  currency: string;
  amount: string;
  network: string;
}

interface PaymentSimulationData {
  paymentId: string;
  status: string;
  trackingStatus: string;
}

const SimulationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { paymentRequests: payments } = useSelector((state: RootState) => state.adminPayment);
  const [activeTab, setActiveTab] = useState<'fund' | 'payment' | 'tracking' | 'balance'>('fund');

  // Fund account form
  const [fundForm, setFundForm] = useState<SimulationFormData>({
    currency: 'USDC',
    amount: '',
    network: 'Ethereum'
  });

  // Payment simulation form
  const [paymentForm, setPaymentForm] = useState<PaymentSimulationData>({
    paymentId: '',
    status: 'payout_completed',
    trackingStatus: ''
  });

  // Tracking simulation form
  const [trackingForm, setTrackingForm] = useState<PaymentSimulationData>({
    paymentId: '',
    status: '',
    trackingStatus: 'en_route_trackable'
  });

  // Results and loading states
  const [fundResult, setFundResult] = useState<any>(null);
  const [paymentResult, setPaymentResult] = useState<any>(null);
  const [trackingResult, setTrackingResult] = useState<any>(null);
  const [balanceResult, setBalanceResult] = useState<any>(null);
  const [fundLoading, setFundLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Error states and retry counts
  const [fundError, setFundError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [fundRetryCount, setFundRetryCount] = useState(0);
  const [paymentRetryCount, setPaymentRetryCount] = useState(0);
  const [trackingRetryCount, setTrackingRetryCount] = useState(0);
  const [balanceRetryCount, setBalanceRetryCount] = useState(0);

  useEffect(() => {
    dispatch(fetchAdminPaymentRequests());
  }, [dispatch]);

  const handleFundAccount = async (retryCount = 0) => {
    setFundLoading(true);
    setFundError(null);

    try {
      const response = await simulationApi.simulateFundAccount({
        currency: fundForm.currency,
        amount: parseFloat(fundForm.amount),
        network: fundForm.network
      });
      setFundResult(response);
      setFundRetryCount(0); // Reset retry count on success
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setFundError(errorMessage);
      setFundResult({ error: errorMessage });

      // Auto-retry logic for network errors (max 3 retries)
      if (retryCount < 3 && (!error.response || error.response.status >= 500)) {
        setFundRetryCount(retryCount + 1);
        setTimeout(() => handleFundAccount(retryCount + 1), Math.pow(2, retryCount) * 1000); // Exponential backoff
        return;
      }
    } finally {
      setFundLoading(false);
    }
  };

  const handlePaymentSimulation = async (retryCount = 0) => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      const response = await simulationApi.simulatePaymentLifecycle(paymentForm.paymentId, { status: paymentForm.status });
      setPaymentResult(response);
      setPaymentRetryCount(0);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setPaymentError(errorMessage);
      setPaymentResult({ error: errorMessage });

      if (retryCount < 3 && (!error.response || error.response.status >= 500)) {
        setPaymentRetryCount(retryCount + 1);
        setTimeout(() => handlePaymentSimulation(retryCount + 1), Math.pow(2, retryCount) * 1000);
        return;
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleTrackingSimulation = async (retryCount = 0) => {
    setTrackingLoading(true);
    setTrackingError(null);

    try {
      const response = await simulationApi.simulateTrackingLifecycle(trackingForm.paymentId, { trackingStatus: trackingForm.trackingStatus });
      setTrackingResult(response);
      setTrackingRetryCount(0);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setTrackingError(errorMessage);
      setTrackingResult({ error: errorMessage });

      if (retryCount < 3 && (!error.response || error.response.status >= 500)) {
        setTrackingRetryCount(retryCount + 1);
        setTimeout(() => handleTrackingSimulation(retryCount + 1), Math.pow(2, retryCount) * 1000);
        return;
      }
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleBalanceCheck = async (retryCount = 0) => {
    setBalanceLoading(true);
    setBalanceError(null);

    try {
      const response = await simulationApi.getAccountBalance();
      setBalanceResult(response);
      setBalanceRetryCount(0);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setBalanceError(errorMessage);
      setBalanceResult({ error: errorMessage });

      if (retryCount < 3 && (!error.response || error.response.status >= 500)) {
        setBalanceRetryCount(retryCount + 1);
        setTimeout(() => handleBalanceCheck(retryCount + 1), Math.pow(2, retryCount) * 1000);
        return;
      }
    } finally {
      setBalanceLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'fund':
        return (
          <div className="simulation-section">
            <h2>Fund Account Simulation</h2>
            <p>Simulate funding your sandbox account with USDC or USDT</p>
            <form className="simulation-form" onSubmit={(e) => { e.preventDefault(); handleFundAccount(); }}>
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={fundForm.currency}
                  onChange={(e) => setFundForm({ ...fundForm, currency: e.target.value })}
                  aria-label="Currency"
                >
                  <option value="USDC">USDC</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={fundForm.amount}
                  onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div className="form-group">
                <label>Network</label>
                <select
                  value={fundForm.network}
                  onChange={(e) => setFundForm({ ...fundForm, network: e.target.value })}
                  aria-label="Network"
                >
                  <option value="Ethereum">Ethereum</option>
                  <option value="Polygon PoS">Polygon PoS</option>
                  <option value="Tron">Tron</option>
                  <option value="Solana">Solana</option>
                </select>
              </div>
              <button type="submit" disabled={fundLoading}>
                {fundLoading ? `Funding...${fundRetryCount > 0 ? ` (Retry ${fundRetryCount}/3)` : ''}` : 'Fund Account'}
              </button>
              {fundError && fundRetryCount < 3 && (
                <button
                  type="button"
                  onClick={() => handleFundAccount()}
                  disabled={fundLoading}
                  className="retry-button"
                >
                  Retry Manually
                </button>
              )}
            </form>
            {fundResult && (
              <div className={`result ${fundResult.error ? 'error' : 'success'}`}>
                <h3>Result</h3>
                {fundError && fundRetryCount >= 3 && (
                  <div className="error-notice">
                    <strong>Max retries reached.</strong> Please check your connection and try again later.
                  </div>
                )}
                <pre>{JSON.stringify(fundResult, null, 2)}</pre>
              </div>
            )}
          </div>
        );

      case 'payment':
        return (
          <div className="simulation-section">
            <h2>Payment Status Simulation</h2>
            <p>Simulate payment status changes for testing</p>
            <form className="simulation-form" onSubmit={(e) => { e.preventDefault(); handlePaymentSimulation(); }}>
              <div className="form-group">
                <label>Payment ID</label>
                <select
                  value={paymentForm.paymentId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentId: e.target.value })}
                  required
                  aria-label="Payment ID"
                >
                  <option value="">Select Payment</option>
                  {payments.map((payment: any) => (
                    <option key={payment._id} value={payment._id}>
                      {payment._id} - {payment.amount} {payment.currency}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={paymentForm.status}
                  onChange={(e) => setPaymentForm({ ...paymentForm, status: e.target.value })}
                  aria-label="Status"
                >
                  <option value="payout_completed">Payout Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="requires_action">Requires Action</option>
                </select>
              </div>
              <button type="submit" disabled={paymentLoading}>
                {paymentLoading ? `Updating...${paymentRetryCount > 0 ? ` (Retry ${paymentRetryCount}/3)` : ''}` : 'Update Payment Status'}
              </button>
              {paymentError && paymentRetryCount < 3 && (
                <button
                  type="button"
                  onClick={() => handlePaymentSimulation()}
                  disabled={paymentLoading}
                  className="retry-button"
                >
                  Retry Manually
                </button>
              )}
            </form>
            {paymentResult && (
              <div className={`result ${paymentResult.error ? 'error' : 'success'}`}>
                <h3>Result</h3>
                {paymentError && paymentRetryCount >= 3 && (
                  <div className="error-notice">
                    <strong>Max retries reached.</strong> Please check your connection and try again later.
                  </div>
                )}
                <pre>{JSON.stringify(paymentResult, null, 2)}</pre>
              </div>
            )}
          </div>
        );

      case 'tracking':
        return (
          <div className="simulation-section">
            <h2>Tracking Status Simulation</h2>
            <p>Simulate tracking status updates for payments</p>
            <form className="simulation-form" onSubmit={(e) => { e.preventDefault(); handleTrackingSimulation(); }}>
              <div className="form-group">
                <label>Payment ID</label>
                <select
                  value={trackingForm.paymentId}
                  onChange={(e) => setTrackingForm({ ...trackingForm, paymentId: e.target.value })}
                  required
                  aria-label="Payment ID"
                >
                  <option value="">Select Payment</option>
                  {payments.map((payment: any) => (
                    <option key={payment._id} value={payment._id}>
                      {payment._id} - {payment.amount} {payment.currency}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tracking Status</label>
                <select
                  value={trackingForm.trackingStatus}
                  onChange={(e) => setTrackingForm({ ...trackingForm, trackingStatus: e.target.value })}
                  aria-label="Tracking Status"
                >
                  <option value="en_route_trackable">En Route (Trackable)</option>
                  <option value="en_route_not_trackable">En Route (Not Trackable)</option>
                  <option value="arrived_at_recipient">Arrived at Recipient</option>
                </select>
              </div>
              <button type="submit" disabled={trackingLoading}>
                {trackingLoading ? `Updating...${trackingRetryCount > 0 ? ` (Retry ${trackingRetryCount}/3)` : ''}` : 'Update Tracking Status'}
              </button>
              {trackingError && trackingRetryCount < 3 && (
                <button
                  type="button"
                  onClick={() => handleTrackingSimulation()}
                  disabled={trackingLoading}
                  className="retry-button"
                >
                  Retry Manually
                </button>
              )}
            </form>
            {trackingResult && (
              <div className={`result ${trackingResult.error ? 'error' : 'success'}`}>
                <h3>Result</h3>
                {trackingError && trackingRetryCount >= 3 && (
                  <div className="error-notice">
                    <strong>Max retries reached.</strong> Please check your connection and try again later.
                  </div>
                )}
                <pre>{JSON.stringify(trackingResult, null, 2)}</pre>
              </div>
            )}
          </div>
        );

      case 'balance':
        return (
          <div className="simulation-section">
            <h2>Account Balance Check</h2>
            <p>Check current sandbox account balance</p>
            <button onClick={() => handleBalanceCheck()} disabled={balanceLoading}>
              {balanceLoading ? `Checking...${balanceRetryCount > 0 ? ` (Retry ${balanceRetryCount}/3)` : ''}` : 'Check Balance'}
            </button>
            {balanceError && balanceRetryCount < 3 && (
              <button
                type="button"
                onClick={() => handleBalanceCheck()}
                disabled={balanceLoading}
                className="retry-button"
              >
                Retry Manually
              </button>
            )}
            {balanceResult && (
              <div className={`result ${balanceResult.error ? 'error' : 'success'}`}>
                <h3>Result</h3>
                {balanceError && balanceRetryCount >= 3 && (
                  <div className="error-notice">
                    <strong>Max retries reached.</strong> Please check your connection and try again later.
                  </div>
                )}
                <pre>{JSON.stringify(balanceResult, null, 2)}</pre>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="simulation-page">
      <div className="simulation-header">
        <h1>Reap API Simulations</h1>
        <p>Test payment flows and integrations in a sandbox environment</p>
      </div>

      <div className="simulation-tabs">
        <button
          className={activeTab === 'fund' ? 'active' : ''}
          onClick={() => setActiveTab('fund')}
        >
          Fund Account
        </button>
        <button
          className={activeTab === 'payment' ? 'active' : ''}
          onClick={() => setActiveTab('payment')}
        >
          Payment Status
        </button>
        <button
          className={activeTab === 'tracking' ? 'active' : ''}
          onClick={() => setActiveTab('tracking')}
        >
          Tracking Status
        </button>
        <button
          className={activeTab === 'balance' ? 'active' : ''}
          onClick={() => setActiveTab('balance')}
        >
          Account Balance
        </button>
      </div>

      <div className="simulation-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default SimulationPage;
